import apiService from './apiService';

/**
 * Manual energy entry: sites, meters, utility bills and production figures.
 * Every method returns the ServiceResponse body, so callers check `success` before `data`.
 */
export default class EnergyApiService {

    async getSites (projectId) {
        const response = await apiService.getData(`api/energy/locations/${projectId}/sites`);
        return response.data;
    }

    async getMeters (siteId) {
        const response = await apiService.getData(`api/energy/sites/${siteId}/meters`);
        return response.data;
    }

    async getUnits () {
        const response = await apiService.getData('api/energy/units');
        return response.data;
    }

    async getDashboardPerformanceSummary () {
        const response = await apiService.getData('api/energy/dashboard/performance-summary');
        return response.data;
    }

    /**
     * Bills on the site's meters overlapping the range.
     * @param siteId {number}
     * @param from {string} ISO date, inclusive
     * @param to {string} ISO date, inclusive
     */
    async getBills (siteId, from, to) {
        const response = await apiService.getData(`api/energy/sites/${siteId}/bills?from=${from}&to=${to}`);
        return response.data;
    }

    /** Year of the site's earliest bill or production record; data is null when it has none. */
    async getFirstDataYear (siteId) {
        const response = await apiService.getData(`api/energy/sites/${siteId}/first-data-year`);
        return response.data;
    }

    /** Saves every changed row on one meter; the server rejects the batch if any period is bad. */
    async saveBills (meterId, bills) {
        const response = await apiService.putData(`api/energy/meters/${meterId}/bills`, { bills });
        return response.data;
    }

    async deleteBill (billId) {
        const response = await apiService.deleteData(`api/energy/bills/${billId}`);
        return response.data;
    }

    async getProduction (projectId, from, to, siteId) {
        const siteFilter = siteId ? `&siteId=${siteId}` : '';
        const response = await apiService.getData(`api/energy/locations/${projectId}/production?from=${from}&to=${to}${siteFilter}`);
        return response.data;
    }

    async saveProduction (projectId, records) {
        const response = await apiService.putData(`api/energy/locations/${projectId}/production`, { records });
        return response.data;
    }

    async deleteProductionRecord (recordId) {
        const response = await apiService.deleteData(`api/energy/production/${recordId}`);
        return response.data;
    }

    /**
     * The location's spreadsheet template for the range. Returns the raw response: `data` is the
     * file as a Blob on success, or a Blob holding the ServiceResponse JSON on failure.
     */
    async downloadImportTemplate (projectId, from, to) {
        return await apiService.getData(
            `api/energy/locations/${projectId}/import-template?from=${from}&to=${to}`,
            { responseType: 'blob' });
    }

    /** What each row of the uploaded template would do; saves nothing. */
    async previewImport (projectId, file) {
        const form = new FormData();
        form.append('file', file);
        const response = await apiService.postFormData(`api/energy/locations/${projectId}/import/preview`, form);
        return response?.data;
    }

    /** Saves the uploaded template's new and changed rows. The server reads the file again rather than trusting the preview. */
    async commitImport (projectId, file) {
        const form = new FormData();
        form.append('file', file);
        const response = await apiService.postFormData(`api/energy/locations/${projectId}/import`, form);
        return response?.data;
    }
}
